/**
 * @fileoverview Custom Room Create Modal
 * Full manual room creation with canvas positioning
 * @author Hospital Room Scheduler Team
 * @created 16-10-2025 12:45:00
 * 
 * This modal allows coordinators to create rooms from scratch without templates.
 * All fields are manually specified including dimensions and canvas position.
 * Uses the same API endpoint as template-based creation but without templateId.
 */

import React, { useState } from 'react';
import {
  Modal,
  Stack,
  Text,
  Button,
  Group,
  NumberInput,
  TextInput,
  Textarea,
  MultiSelect,
  Divider,
  Paper,
  Alert
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconDoor, IconRuler, IconUsers, IconInfoCircle, IconMapPin } from '@tabler/icons-react';
import { useCreateRoomWithPosition } from '../../hooks/admin/useRooms';
import { useSessionTemplates } from '../../hooks/api/useSessionTemplates';
import { VALID_SPECIALTIES, getSpecialtyDisplayName } from '@mchd/shared';

// =============================================================================
// INTERFACES
// =============================================================================

interface CustomRoomCreateModalProps {
  floorId: string;
  opened: boolean;
  onClose: () => void;
}

// =============================================================================
// COMPONENT
// =============================================================================

/**
 * Custom Room Create Modal
 * 
 * Allows creating rooms with full manual control over all properties.
 * Used in floor plan editor when coordinator wants to create a room from scratch
 * without using predefined templates.
 * 
 * @param props - Component props
 * @returns React component
 */
export const CustomRoomCreateModal: React.FC<CustomRoomCreateModalProps> = ({
  floorId,
  opened,
  onClose
}) => {
  const createMutation = useCreateRoomWithPosition();
  const { data: sessionTemplatesData, isLoading: sessionTemplatesLoading } = useSessionTemplates();

  // Form setup with validation
  const form = useForm({
    initialValues: {
      number: '',
      name: '',
      capacity: 5,
      width: 150,
      height: 120,
      canvasX: 100,
      canvasY: 100,
      description: '',
      allowedSpecialties: [] as string[],
      defaultBuffer: 15,
      sessionTemplateIds: [] as string[]
    },
    validate: {
      number: (value) => {
        if (!value || !value.trim()) return 'Room number is required';
        if (value.length > 20) return 'Room number cannot exceed 20 characters';
        return null;
      },
      name: (value) => {
        if (!value || !value.trim()) return 'Room name is required';
        if (value.length < 2) return 'Room name must be at least 2 characters';
        if (value.length > 100) return 'Room name cannot exceed 100 characters';
        return null;
      },
      capacity: (value) => {
        if (!value || value < 1) return 'Capacity must be at least 1';
        if (value > 100) return 'Capacity cannot exceed 100';
        return null;
      },
      width: (value) => {
        if (!value || value < 50) return 'Width must be at least 50';
        if (value > 500) return 'Width cannot exceed 500';
        return null;
      },
      height: (value) => {
        if (!value || value < 50) return 'Height must be at least 50';
        if (value > 500) return 'Height cannot exceed 500';
        return null;
      },
      canvasX: (value) => {
        if (value < 0) return 'X position cannot be negative';
        if (value > 2000) return 'X position too large';
        return null;
      },
      canvasY: (value) => {
        if (value < 0) return 'Y position cannot be negative';
        if (value > 2000) return 'Y position too large';
        return null;
      },
      description: (value) => {
        if (value && value.length > 500) return 'Description cannot exceed 500 characters';
        return null;
      },
      defaultBuffer: (value) => {
        if (value < 0) return 'Buffer cannot be negative';
        if (value > 120) return 'Buffer cannot exceed 120 minutes';
        return null;
      }
    }
  });

  /**
   * Handle form submission
   * Creates room with all custom specifications
   */
  const handleSubmit = async (values: typeof form.values) => {
    try {
      await createMutation.mutateAsync({
        floorId,
        room: {
          number: values.number.trim(),
          name: values.name.trim(),
          capacity: values.capacity
          // NO templateId - this is custom creation
          // Note: sessionTemplateIds, description, allowedSpecialties, defaultBuffer
          // are not supported by the current API - would need backend changes
        },
        position: {
          x: values.canvasX,
          y: values.canvasY,
          width: values.width,
          height: values.height
        }
      });

      // Reset form and close
      form.reset();
      onClose();
    } catch (error: any) {
      // Error already handled by mutation hook with notifications
      console.error('Failed to create custom room:', error.message || error);
    }
  };

  /**
   * Handle modal close with form reset
   */
  const handleClose = () => {
    form.reset();
    onClose();
  };

  /**
   * Generate specialty options for MultiSelect
   */
  const specialtyOptions = VALID_SPECIALTIES.map(specialty => ({
    value: specialty,
    label: getSpecialtyDisplayName(specialty)
  }));

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title="Create Custom Room"
      size="lg"
      centered
      zIndex={1200}
      overlayProps={{
        backgroundOpacity: 0.55,
        blur: 3
      }}
      styles={{
        inner: { zIndex: 1200 },
        content: { zIndex: 1201 }
      }}
    >
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap="md">
          {/* Info Alert */}
          <Alert icon={<IconInfoCircle size={16} />} color="blue" variant="light">
            <Text size="sm">
              Create a room with custom specifications. All fields are manually configured.
            </Text>
          </Alert>

          {/* Basic Information */}
          <Paper p="md" radius="md" withBorder>
            <Group gap="xs" mb="md">
              <IconDoor size={18} />
              <Text fw={600} size="sm">Room Information</Text>
            </Group>
            
            <Stack gap="md">
              <Group grow>
                <TextInput
                  label="Room Number"
                  placeholder="e.g., A101, B205"
                  description="Unique identifier for this room"
                  required
                  {...form.getInputProps('number')}
                />
                <TextInput
                  label="Room Name"
                  placeholder="e.g., Operating Room 1"
                  description="Descriptive name for the room"
                  required
                  {...form.getInputProps('name')}
                />
              </Group>

              <Group grow>
                <NumberInput
                  label="Capacity"
                  placeholder="Maximum occupancy"
                  description="Maximum number of people"
                  min={1}
                  max={100}
                  required
                  leftSection={<IconUsers size={16} />}
                  {...form.getInputProps('capacity')}
                />
                <NumberInput
                  label="Default Buffer (minutes)"
                  placeholder="Time between sessions"
                  description="Cleaning/preparation time"
                  min={0}
                  max={120}
                  step={5}
                  {...form.getInputProps('defaultBuffer')}
                />
              </Group>

              <Textarea
                label="Description"
                placeholder="Optional room description"
                description="Additional details about this room"
                minRows={2}
                maxRows={4}
                {...form.getInputProps('description')}
              />

              <MultiSelect
                label="Allowed Specialties"
                placeholder="Select medical specialties"
                description="Which specialties can use this room (optional)"
                data={specialtyOptions}
                searchable
                clearable
                {...form.getInputProps('allowedSpecialties')}
              />

              {/* TODO: Session Templates - Backend API needs to be updated to support sessionTemplateIds
              <MultiSelect
                label="Session Templates"
                placeholder="Select session templates"
                description="Which session times are available for this room (optional)"
                data={sessionTemplatesData?.data?.map((template: any) => ({
                  value: template.id,
                  label: `${template.name} (${template.startTime} - ${template.endTime})`
                })) || []}
                searchable
                clearable
                disabled={sessionTemplatesLoading}
                {...form.getInputProps('sessionTemplateIds')}
              />
              */}
            </Stack>
          </Paper>

          {/* Canvas Dimensions */}
          <Paper p="md" radius="md" withBorder>
            <Group gap="xs" mb="md">
              <IconRuler size={18} />
              <Text fw={600} size="sm">Canvas Dimensions</Text>
            </Group>
            
            <Stack gap="md">
              <Group grow>
                <NumberInput
                  label="Width"
                  placeholder="Room width"
                  description="Canvas width in pixels"
                  min={50}
                  max={500}
                  step={10}
                  required
                  {...form.getInputProps('width')}
                />
                <NumberInput
                  label="Height"
                  placeholder="Room height"
                  description="Canvas height in pixels"
                  min={50}
                  max={500}
                  step={10}
                  required
                  {...form.getInputProps('height')}
                />
              </Group>

              <Text size="xs" c="dimmed">
                Recommended dimensions: 100-200 pixels. Larger rooms use 200-300.
              </Text>
            </Stack>
          </Paper>

          {/* Canvas Position */}
          <Paper p="md" radius="md" withBorder>
            <Group gap="xs" mb="md">
              <IconMapPin size={18} />
              <Text fw={600} size="sm">Canvas Position</Text>
            </Group>
            
            <Stack gap="md">
              <Group grow>
                <NumberInput
                  label="X Position"
                  placeholder="Horizontal position"
                  description="Distance from left edge"
                  min={0}
                  max={2000}
                  step={10}
                  required
                  {...form.getInputProps('canvasX')}
                />
                <NumberInput
                  label="Y Position"
                  placeholder="Vertical position"
                  description="Distance from top edge"
                  min={0}
                  max={2000}
                  step={10}
                  required
                  {...form.getInputProps('canvasY')}
                />
              </Group>

              <Text size="xs" c="dimmed">
                Position determines where the room appears on the floor plan. You can adjust this later by dragging.
              </Text>
            </Stack>
          </Paper>

          {/* Form Actions */}
          <Divider />
          
          <Group justify="flex-end" gap="sm">
            <Button
              variant="subtle"
              onClick={handleClose}
              disabled={createMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              loading={createMutation.isPending}
              leftSection={<IconDoor size={16} />}
            >
              Create Custom Room
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
};

